import React, { useState, useCallback } from 'react';
import { api, useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Lock, Unlock, Trash2, Users, AlertTriangle, Loader2, CheckCircle2,
  Eye, Pencil, Save, X, ChevronDown, ChevronUp, Volume2, Mic,
  ListChecks, Clapperboard, CheckCircle, XCircle, BookOpen,
  FileText, PlayCircle, PenLine
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── Module-aware helpers ───────────────────────────────────────────────────
const MODULE_META = {
  tts: { label: 'Text to Speech', Icon: Volume2,   color: 'sky',    desc: 'Read aloud' },
  stt: { label: 'Speech to Text', Icon: Mic,        color: 'emerald', desc: 'Listen & type' },
  mcq: { label: 'MCQ Quiz',       Icon: ListChecks, color: 'amber',  desc: 'Multiple choice' },
  vmcq:{ label: 'Cinematic MCQ',  Icon: Clapperboard,color: 'purple', desc: 'Video + MCQ' },
  ftb: { label: 'Fill the Blanks', Icon: PenLine,   color: 'orange', desc: 'Fill in blanks' },
};

// ─── Preview: renders a single question as student would see it ───────────────
const QuestionPreview = ({ question, index, moduleType }) => {
  const [expanded, setExpanded] = useState(true);
  const optionLetters = ['A', 'B', 'C', 'D'];

  if (moduleType === 'tts') {
    return (
      <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800/60">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 text-xs font-bold flex items-center justify-center">{index + 1}</span>
          <div className="flex-1">
            <p className="text-slate-800 dark:text-white font-medium leading-relaxed">{question.question_text}</p>
            <div className="mt-2 flex items-center gap-2 text-xs text-sky-600 dark:text-sky-400">
              <Volume2 className="h-3.5 w-3.5" />
              <span>Student reads this sentence aloud</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (moduleType === 'stt') {
    return (
      <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800/60">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-bold flex items-center justify-center">{index + 1}</span>
          <div className="flex-1">
            <p className="text-slate-800 dark:text-white font-medium leading-relaxed">{question.question_text}</p>
            <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
              <Mic className="h-3.5 w-3.5" />
              <span>System speaks this • Student types what they hear</span>
            </div>
            <div className="mt-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-600 text-xs text-slate-400 italic">
              [ Student's answer field ]
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (moduleType === 'mcq') {
    const opts = question.options || [];
    return (
      <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800/60">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-bold flex items-center justify-center">{index + 1}</span>
          <div className="flex-1">
            <p className="text-slate-800 dark:text-white font-medium mb-3 leading-relaxed">{question.question_text}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {opts.map((opt, oi) => {
                const isCorrect = oi === question.correct_option;
                return (
                  <div key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                    isCorrect
                      ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                  }`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                    }`}>{optionLetters[oi]}</span>
                    <span className="flex-1">{opt}</span>
                    {isCorrect && <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
            {question.explanation && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300">
                <span className="font-semibold">Explanation: </span>{question.explanation}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (moduleType === 'vmcq') {
    const opts = question.options || [];
    const mediaPath = question.media_path || question.image_path;
    const mediaType = question.media_type || (mediaPath?.match(/\.(mp4|webm|mov)$/i) ? 'video' : 'image');
    return (
      <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800/60">
        {mediaPath && (
          <div className="bg-slate-900 flex items-center justify-center" style={{ maxHeight: 180 }}>
            {mediaType === 'video' ? (
              <video src={`${BACKEND_URL}${mediaPath}`} controls className="w-full max-h-44 object-contain" />
            ) : (
              <img src={`${BACKEND_URL}${mediaPath}`} alt="media" className="w-full max-h-44 object-contain" />
            )}
          </div>
        )}
        <div className="p-4">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-bold flex items-center justify-center">{index + 1}</span>
            <div className="flex-1">
              <p className="text-slate-800 dark:text-white font-medium mb-3 leading-relaxed">{question.question_text}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {opts.map((opt, oi) => {
                  const isCorrect = oi === question.correct_option;
                  return (
                    <div key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm ${
                      isCorrect
                        ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                    }`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                      }`}>{optionLetters[oi]}</span>
                      <span className="flex-1">{opt}</span>
                      {isCorrect && <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
                    </div>
                  );
                })}
              </div>
              {question.explanation && (
                <div className="mt-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300">
                  <span className="font-semibold">Explanation: </span>{question.explanation}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (moduleType === 'ftb') {
    const correctAnswers = question.correct_answers || [];
    // Render sentence with blanks highlighted
    const parts = question.question_text.split(/(___+)/g);
    let blankIndex = 0;
    
    return (
      <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800/60">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 text-xs font-bold flex items-center justify-center">{index + 1}</span>
          <div className="flex-1">
            <div className="text-slate-800 dark:text-white font-medium mb-3 leading-relaxed flex flex-wrap items-center gap-1">
              {parts.map((part, idx) => {
                if (part.match(/___+/)) {
                  const answer = correctAnswers[blankIndex] || '___';
                  blankIndex++;
                  return (
                    <span key={idx} className="px-3 py-1 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-semibold border-2 border-emerald-400">
                      {answer}
                    </span>
                  );
                }
                return <span key={idx}>{part}</span>;
              })}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400">
              <PenLine className="h-3.5 w-3.5" />
              <span>Student fills in the blanks</span>
            </div>
            {question.explanation && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300">
                <span className="font-semibold">Explanation: </span>{question.explanation}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// ─── Edit single question inline row ─────────────────────────────────────────
const EditQuestionRow = ({ question, index, moduleType, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    question_text: question.question_text || '',
    options: question.options ? [...question.options] : ['', '', '', ''],
    correct_option: question.correct_option ?? 0,
    explanation: question.explanation || '',
  });
  const optionLetters = ['A', 'B', 'C', 'D'];

  const handleSave = async () => {
    if (!form.question_text.trim()) { toast.error('Question text is required'); return; }
    setSaving(true);
    try {
      const payload = { question_text: form.question_text.trim() };
      if (moduleType === 'mcq' || moduleType === 'vmcq') {
        payload.options = form.options.map(o => o.trim()).filter(Boolean);
        payload.correct_option = form.correct_option;
        payload.explanation = form.explanation.trim() || null;
      }
      await api.put(`/questions/${question.id}`, payload);
      toast.success('Question updated');
      setEditing(false);
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      question_text: question.question_text || '',
      options: question.options ? [...question.options] : ['', '', '', ''],
      correct_option: question.correct_option ?? 0,
      explanation: question.explanation || '',
    });
    setEditing(false);
  };

  const isMCQ = moduleType === 'mcq' || moduleType === 'vmcq';
  const isFTB = moduleType === 'ftb';
  const colorMap = { tts: 'sky', stt: 'emerald', mcq: 'amber', vmcq: 'purple', ftb: 'orange' };
  const color = colorMap[moduleType] || 'indigo';

  return (
    <div className={`border-2 rounded-xl transition-all ${editing ? `border-${color}-400 dark:border-${color}-500 bg-${color}-50/30 dark:bg-${color}-900/10` : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60'}`}>
      {/* Question Header */}
      <div className="flex items-start gap-3 p-3">
        <span className={`flex-shrink-0 w-7 h-7 rounded-full bg-${color}-100 dark:bg-${color}-900/40 text-${color}-700 dark:text-${color}-300 text-xs font-bold flex items-center justify-center mt-0.5`}>
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          {editing ? (
            <Textarea
              value={form.question_text}
              onChange={e => setForm(p => ({ ...p, question_text: e.target.value }))}
              className="text-sm w-full min-h-[60px]"
              placeholder="Question text..."
            />
          ) : (
            <p className="text-sm text-slate-800 dark:text-white leading-relaxed">{question.question_text}</p>
          )}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
              <Button size="sm" className={`h-7 px-2 text-xs bg-${color}-600 hover:bg-${color}-700 text-white`} onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save</>}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" onClick={() => onDelete(question.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* MCQ/VMCQ options (visible in edit mode) */}
      {editing && isMCQ && (
        <div className="px-4 pb-3 space-y-2">
          <Label className="text-xs text-slate-500">Options (select correct answer)</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[0, 1, 2, 3].map(oi => (
              <div key={oi} className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all ${form.correct_option === oi ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                onClick={() => setForm(p => ({ ...p, correct_option: oi }))}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 cursor-pointer ${form.correct_option === oi ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
                  {optionLetters[oi]}
                </span>
                <Input
                  value={form.options[oi] || ''}
                  onChange={e => {
                    const opts = [...form.options];
                    opts[oi] = e.target.value;
                    setForm(p => ({ ...p, options: opts }));
                  }}
                  onClick={e => e.stopPropagation()}
                  className="h-7 text-xs border-0 p-0 bg-transparent focus-visible:ring-0 flex-1"
                  placeholder={`Option ${optionLetters[oi]}...`}
                />
                {form.correct_option === oi && <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <Label className="text-xs text-slate-500">Explanation (optional)</Label>
            <Input
              value={form.explanation}
              onChange={e => setForm(p => ({ ...p, explanation: e.target.value }))}
              className="text-xs mt-1"
              placeholder="Why is this the correct answer?"
            />
          </div>
        </div>
      )}

      {/* MCQ/VMCQ options (view mode) */}
      {!editing && isMCQ && question.options?.length > 0 && (
        <div className="px-4 pb-3">
          <div className="grid grid-cols-2 gap-1.5">
            {question.options.map((opt, oi) => (
              <div key={oi} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${oi === question.correct_option ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium' : 'bg-slate-50 dark:bg-slate-800 text-slate-500'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-xs ${oi === question.correct_option ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>{optionLetters[oi]}</span>
                <span className="truncate">{opt}</span>
                {oi === question.correct_option && <CheckCircle className="h-3 w-3 text-emerald-500 ml-auto flex-shrink-0" />}
              </div>
            ))}
          </div>
          {question.explanation && (
            <p className="mt-1.5 text-xs text-blue-600 dark:text-blue-400 italic">💡 {question.explanation}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
const TestManagementPanel = ({ tests, classes, sections, onRefresh, moduleColor = 'indigo', moduleType }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isTeacher = user?.role === 'teacher';
  const canEdit = isTeacher;

  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [classAccessTest, setClassAccessTest] = useState(null);

  // Preview state
  const [previewTest, setPreviewTest] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Edit questions state
  const [editQTest, setEditQTest] = useState(null);
  const [editQLoading, setEditQLoading] = useState(false);

  const getSectionIdsForClasses = (classIds) => {
    if (!classIds?.length) return [];
    if (!sections || sections.length === 0) return classIds;
    const ids = sections.filter(s => classIds.includes(s.class_id)).map(s => s.id);
    return ids.length > 0 ? ids : classIds;
  };

  // ── Lock / Unlock ───────────────────────────────────────────────────────
  const handleLock = async (test) => {
    setActionLoading(test.id);
    try {
      await api.put(`/tests/${test.id}/lock`);
      toast.success(`"${test.test_name}" locked`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock test');
    } finally { setActionLoading(null); }
  };

  const handleUnlock = async (test) => {
    setActionLoading(test.id);
    try {
      await api.put(`/tests/${test.id}/unlock`);
      toast.success(`"${test.test_name}" unlocked`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock test');
    } finally { setActionLoading(null); }
  };

  const handleDelete = async (testId) => {
    setActionLoading(testId);
    try {
      await api.delete(`/tests/${testId}`);
      toast.success('Test deleted');
      setDeleteConfirm(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete test');
    } finally { setActionLoading(null); }
  };

  const handleClassAccessSave = async () => {
    if (!classAccessTest) return;
    const sectionIds = getSectionIdsForClasses(classAccessTest.selected_class_ids);
    setActionLoading(classAccessTest.test.id);
    try {
      await api.put(`/tests/${classAccessTest.test.id}`, {
        section_ids: sectionIds.length > 0 ? sectionIds : classAccessTest.selected_class_ids
      });
      toast.success('Class access updated');
      setClassAccessTest(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to update class access');
    } finally { setActionLoading(null); }
  };

  // ── Preview ──────────────────────────────────────────────────────────────
  const openPreview = async (test) => {
    setPreviewLoading(true);
    try {
      const res = await api.get(`/tests/${test.id}`);
      setPreviewTest(res.data);
    } catch {
      toast.error('Failed to load test preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Edit Questions ───────────────────────────────────────────────────────
  const openEditQuestions = async (test) => {
    setEditQLoading(true);
    try {
      const res = await api.get(`/tests/${test.id}`);
      setEditQTest(res.data);
    } catch {
      toast.error('Failed to load test for editing');
    } finally {
      setEditQLoading(false);
    }
  };

  const handleDeleteQuestionFromEdit = async (questionId) => {
    if (!window.confirm('Remove this question from the test?')) return;
    try {
      const updatedIds = editQTest.questions.filter(q => q.id !== questionId).map(q => q.id);
      await api.put(`/tests/${editQTest.id}`, { question_ids: updatedIds });
      toast.success('Question removed');
      // Refresh edit panel
      const res = await api.get(`/tests/${editQTest.id}`);
      setEditQTest(res.data);
      onRefresh();
    } catch {
      toast.error('Failed to remove question');
    }
  };

  const handleQuestionSaved = async () => {
    // Re-fetch to update edit panel
    try {
      const res = await api.get(`/tests/${editQTest.id}`);
      setEditQTest(res.data);
    } catch {}
    onRefresh();
  };

  const getAssignedClassIds = (test) => {
    const classIds = new Set();
    if (test.classes?.length) {
      test.classes.forEach(cls => classIds.add(cls.id));
      return [...classIds];
    }
    if (test.sections) {
      test.sections.forEach(s => {
        if (!sections?.length) classIds.add(s.id);
        else {
          const sec = sections.find(sec => sec.id === s.id || sec.name === s.name);
          if (sec) classIds.add(sec.class_id);
        }
      });
    }
    return [...classIds];
  };

  const getStatusBadge = (test) => {
    if (test.is_locked) return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        <Lock className="h-3 w-3" /> Locked
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        <CheckCircle2 className="h-3 w-3" /> Active
      </span>
    );
  };

  // Detect module type from tests if not passed as prop
  const resolvedModuleType = moduleType || tests?.[0]?.module_type;
  const meta = MODULE_META[resolvedModuleType] || MODULE_META.mcq;
  const ModuleIcon = meta.Icon;

  if (!tests?.length) return (
    <div className="text-center py-8 text-slate-500">No tests created yet.</div>
  );

  return (
    <>
      <div className="space-y-3">
        {tests.map(test => (
          <Card key={test.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-${moduleColor}-100 text-${moduleColor}-700 dark:bg-${moduleColor}-900/40 dark:text-${moduleColor}-300`}>
                      {test.test_number}
                    </span>
                    {getStatusBadge(test)}
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm truncate">{test.test_name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {test.question_count || 0} questions
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap shrink-0 justify-end">
                  {/* Preview button - all roles */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPreview(test)}
                    disabled={previewLoading}
                    className="text-xs h-7 px-2 text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/20"
                    data-testid={`preview-btn-${test.id}`}
                  >
                    {previewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Eye className="h-3 w-3 mr-1" />Preview</>}
                  </Button>
                  {/* Edit Questions button - teacher/admin only */}
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditQuestions(test)}
                      disabled={editQLoading}
                      className="text-xs h-7 px-2 text-violet-600 border-violet-200 hover:bg-violet-50 dark:text-violet-400 dark:border-violet-800 dark:hover:bg-violet-900/20"
                      data-testid={`edit-q-btn-${test.id}`}
                    >
                      {editQLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Pencil className="h-3 w-3 mr-1" />Edit</>}
                    </Button>
                  )}
                  {/* Class Access */}
                  <Button size="sm" variant="outline"
                    onClick={() => setClassAccessTest({ test, selected_class_ids: getAssignedClassIds(test) })}
                    className="text-xs h-7 px-2"
                    data-testid={`class-access-btn-${test.id}`}
                  >
                    <Users className="h-3 w-3 mr-1" />Classes
                  </Button>
                  {/* Lock/Unlock */}
                  <Button size="sm"
                      variant={test.is_locked ? 'default' : 'outline'}
                      onClick={() => test.is_locked ? handleUnlock(test) : handleLock(test)}
                      disabled={actionLoading === test.id}
                    className={`text-xs h-7 px-2 ${test.is_locked ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                    data-testid={`lock-btn-${test.id}`}
                  >
                    {actionLoading === test.id ? <Loader2 className="h-3 w-3 animate-spin" />
                      : test.is_locked ? <><Unlock className="h-3 w-3 mr-1" />Unlock</> : <><Lock className="h-3 w-3 mr-1" />Lock</>}
                  </Button>}
                  {/* Delete */}
                  <Button size="sm" variant="ghost"
                    onClick={() => setDeleteConfirm(test.id)}
                    disabled={actionLoading === test.id}
                    className="text-xs h-7 px-2 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                    data-testid={`delete-btn-${test.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── DELETE CONFIRM DIALOG ── */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />Delete Test
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 dark:text-slate-300 text-sm">
            Are you sure you want to delete this test? Student statistics will still be counted in dashboards.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} disabled={actionLoading === deleteConfirm}>
              {actionLoading === deleteConfirm ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CLASS ACCESS DIALOG ── */}
      {classAccessTest && (
        <Dialog open={true} onOpenChange={() => setClassAccessTest(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Class Access — {classAccessTest.test.test_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Select which classes can access this test:</p>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 max-h-[400px] overflow-y-auto pr-2">
                {classes.map(cls => {
                  const isSelected = classAccessTest.selected_class_ids.includes(cls.id);
                  return (
                    <button key={cls.id}
                      onClick={() => {
                        const ids = classAccessTest.selected_class_ids;
                        const updated = isSelected ? ids.filter(id => id !== cls.id) : [...ids, cls.id];
                        setClassAccessTest(prev => ({ ...prev, selected_class_ids: updated }));
                      }}
                      className={`p-2 rounded-lg border-2 text-sm font-medium transition-all ${isSelected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'}`}
                    >
                      {cls.name?.replace(/^Class\s+/i, '') || cls.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClassAccessTest(null)}>Cancel</Button>
              <Button onClick={handleClassAccessSave} disabled={actionLoading === classAccessTest.test.id}>
                {actionLoading === classAccessTest.test.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Access
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── PREVIEW DIALOG ── */}
      {previewTest && (
        <Dialog open={true} onOpenChange={() => setPreviewTest(null)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
            {/* Header */}
            <div className={`sticky top-0 z-10 px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-${moduleColor}-600/10 to-transparent backdrop-blur`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl bg-${moduleColor}-100 dark:bg-${moduleColor}-900/40`}>
                    <ModuleIcon className={`h-5 w-5 text-${moduleColor}-600 dark:text-${moduleColor}-400`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-${moduleColor}-100 text-${moduleColor}-700 dark:bg-${moduleColor}-900/40 dark:text-${moduleColor}-300`}>
                        {previewTest.test_number}
                      </span>
                      <span className="text-xs text-slate-500">{meta.label}</span>
                    </div>
                    <h2 className="font-bold text-slate-900 dark:text-white text-base mt-0.5">{previewTest.test_name}</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {previewTest.questions?.length || 0} questions ·
                      Classes: {previewTest.classes?.map(c => c.name?.replace(/^Class\s+/i, '')).join(', ') || 'None'}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => setPreviewTest(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-1.5 w-fit">
                <CheckCircle className="h-3.5 w-3.5" />
                <span>Correct answers are highlighted in green</span>
              </div>
            </div>

            {/* Questions */}
            <div className="p-6 space-y-4">
              {(!previewTest.questions || previewTest.questions.length === 0) ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No questions in this test</p>
                </div>
              ) : (
                previewTest.questions.map((q, i) => (
                  <QuestionPreview
                    key={q.id}
                    question={q}
                    index={i}
                    moduleType={resolvedModuleType || previewTest.module_type}
                  />
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── EDIT QUESTIONS DIALOG ── */}
      {editQTest && canEdit && (
        <Dialog open={true} onOpenChange={() => setEditQTest(null)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
            {/* Header */}
            <div className="sticky top-0 z-10 px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 backdrop-blur">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-violet-100 dark:bg-violet-900/40">
                    <Pencil className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                        {editQTest.test_number}
                      </span>
                      <span className="text-xs text-slate-500">{meta.label}</span>
                    </div>
                    <h2 className="font-bold text-slate-900 dark:text-white text-base mt-0.5">{editQTest.test_name}</h2>
                    <p className="text-xs text-slate-500">{editQTest.questions?.length || 0} questions</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => setEditQTest(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 rounded-lg px-3 py-1.5 w-fit">
                <Pencil className="h-3.5 w-3.5" />
                <span>Click the pencil icon on any question to edit it inline</span>
              </div>
            </div>

            {/* Questions */}
            <div className="p-6 space-y-3">
              {(!editQTest.questions || editQTest.questions.length === 0) ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No questions in this test</p>
                </div>
              ) : (
                editQTest.questions.map((q, i) => (
                  <EditQuestionRow
                    key={q.id}
                    question={q}
                    index={i}
                    moduleType={resolvedModuleType || editQTest.module_type}
                    onSave={handleQuestionSaved}
                    onDelete={handleDeleteQuestionFromEdit}
                  />
                ))
              )}
            </div>

            <div className="px-6 pb-6">
              <Button className="w-full" variant="outline" onClick={() => setEditQTest(null)}>
                Done Editing
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default TestManagementPanel;
